import React, { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { CalendarDays, Database, FileSearch, Calculator, ArrowRight, Layers } from 'lucide-react';
import CollateralWorkspace from '../collateral/CollateralWorkspace';
import StratificationPanel from '../collateral/StratificationPanel';
import IngestionWorkspace from '../ingestion/IngestionWorkspace';
import BorrowingBaseWorkspace from '../borrowingBase/BorrowingBaseWorkspace';
import PeriodWorkspace from '../period/PeriodWorkspace';
import { getPeriodWorkspace } from '../period/periodApi';
import { apiErrorMessage } from '../api/abfApi';
import { facilityWorkspacePath } from '../routes';
import type { ActionFeedbackState } from '../components/ActionFeedback';
import type { AbfIngestionDelivery } from '../types/ingestionCenter';

interface Props {
  facilityId: number;
  currency?: string | null;
  servicerName?: string | null;
  userRole?: string | null;
  onFeedback: (feedback: ActionFeedbackState | null) => void;
}

const Navigation = styled.nav`
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
  margin: 18px 0; padding: 8px;
  border: 1px solid rgba(148,163,184,.18); border-radius: 12px;
  background: rgba(8,16,30,.8);
  button {
    display: inline-flex; align-items: center; gap: 7px; min-height: 44px;
    padding: 8px 12px; border: 1px solid rgba(148,163,184,.24); border-radius: 8px;
    color: #cbd5e1; background: rgba(15,23,42,.6); cursor: pointer; font: inherit; font-size: 14px;
  }
  button[aria-pressed='true'] { color: #e0f2fe; border-color: #7dd3fc; background: rgba(14,116,144,.2); }
  button:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 3px; }
  .context { display: flex; align-items: center; gap: 6px; margin-left: auto; padding: 8px; color: #a2b3c9; font-size: 12px; }
  @media(max-width:900px) { .context { margin-left: 0; width: 100%; } }
`;

type CycleView = 'period' | 'ingestion' | 'explorer' | 'strats' | 'details';
const cycleViews: CycleView[] = ['period', 'ingestion', 'explorer', 'strats', 'details'];

/**
 * One Collateral tab for the monthly cycle (F-S2-UI-005). Period review is the default and the
 * only place the month is worked; the other views are the full working areas it connects.
 */
const CollateralCycleWorkspace: React.FC<Props> = props => {
  const { facilityId, onFeedback } = props;
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  const requested = params.get('collateralView') || '';
  const view: CycleView = (cycleViews as string[]).includes(requested) ? requested as CycleView : 'period';
  const select = async (next: CycleView) => {
    const copy = new URLSearchParams(params);
    copy.set('collateralView', next); copy.delete('borrowingBaseView');
    if (next === 'explorer' || next === 'strats') {
      setOpening(true);
      try {
        const workspace = await getPeriodWorkspace(facilityId);
        const requestedPeriod = Number(params.get('period') || params.get('periodId'));
        const period = requestedPeriod ? workspace.periods.find(p => p.id === requestedPeriod)
          : [...workspace.periods].reverse().find(p => p.readiness.phase !== 'APPROVED') || workspace.periods[0];
        if (!period) throw new Error('Select a valid period in Period review first.');
        const explicitTape = Number(params.get('tape'));
        const tape = period.history.tapes.find(t => Number(t.id) === explicitTape) || period.tape || period.history.tapes[0];
        if (!tape) throw new Error(`Period ${period.label} has no tape to explore. Receive its evidence first.`);
        copy.set('period', String(period.id)); copy.delete('periodId'); copy.set('tape', String(tape.id));
      } catch (error) {
        onFeedback({tone:'error', message:apiErrorMessage(error, 'This period’s tape could not be opened.')});
        return;
      } finally { setOpening(false); }
    }
    setParams(copy);
  };
  const openExplorer = useCallback((tapeId: number) => {
    const copy = new URLSearchParams(params);
    copy.set('collateralView', 'explorer'); copy.set('tape', String(tapeId));
    navigate(`${facilityWorkspacePath(facilityId, 'collateral')}?${copy}`);
  }, [params, navigate, facilityId]);
  const reviewDelivery = useCallback(async (delivery: AbfIngestionDelivery) => {
    setOpening(true);
    try {
      const workspace = await getPeriodWorkspace(facilityId);
      const asOf = delivery.business_date.slice(0, 10);
      const period = workspace.periods.find(p => p.history.tapes.some(t => Number(t.id) === delivery.tape_ingest_id))
        || workspace.periods.find(p => p.periodStart <= asOf && p.periodEnd >= asOf);
      if (!period) throw new Error(`No collection period covers ${asOf}. Add the payment calendar on the contract; the delivery remains retained here.`);
      const copy = new URLSearchParams(params);
      copy.set('collateralView', 'period'); copy.set('period', String(period.id)); copy.delete('periodId'); copy.delete('tape');
      copy.set('delivery', String(delivery.manifest_id));
      setParams(copy);
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The delivery’s period could not be opened.') });
    } finally { setOpening(false); }
  }, [facilityId, params, setParams, onFeedback]);

  return <>
    <Navigation aria-label="Collateral workspaces">
      <button type="button" disabled={opening} aria-pressed={view === 'period'} onClick={() => select('period')}><CalendarDays size={16} />Period review</button>
      <button type="button" disabled={opening} aria-pressed={view === 'ingestion'} onClick={() => select('ingestion')}><Database size={16} />Ingestion Center</button>
      <button type="button" disabled={opening} aria-pressed={view === 'explorer'} onClick={() => select('explorer')}><FileSearch size={16} />Collateral Explorer</button>
      <button type="button" disabled={opening} aria-pressed={view === 'strats'} onClick={() => select('strats')}><Layers size={16} />Stratification</button>
      <button type="button" disabled={opening} aria-pressed={view === 'details'} onClick={() => select('details')}><Calculator size={16} />Current base & controls</button>
      <span className="context">Receive evidence <ArrowRight size={12} /> Resolve issues <ArrowRight size={12} /> Review and approve</span>
    </Navigation>
    {view === 'ingestion' ? <IngestionWorkspace {...props} onReviewPeriod={reviewDelivery} openingPeriod={opening} onAddTape={() => select('period')} />
      : view === 'explorer' ? <CollateralWorkspace {...props} userRole={props.userRole || undefined} embedded />
      : view === 'strats' ? <StratificationPanel facilityId={facilityId} currency={props.currency} tapeIngestId={Number(params.get('tape')) || undefined} onFeedback={onFeedback} />
      : view === 'details' ? <><p style={{color:'#a2b3c9',fontSize:13}}>Current facility authority and configuration. For the selected month’s calculation and certificate comparison, use Period review.</p><BorrowingBaseWorkspace {...props} /></>
      : <PeriodWorkspace {...props} onOpenExplorer={openExplorer} />}
  </>;
};
export default CollateralCycleWorkspace;
